/* Immer relies on these 'dirty' tricks: https://redux-toolkit.js.org/api/createSlice#examples
 * note that "state" is already a draft of the original state and meant to be reassigned */
import { createSlice } from '@reduxjs/toolkit';

export type RequestState = {
  id: string | undefined;
  isLoading: boolean | undefined;
  hasError: boolean;
};

export const initialRequestState: RequestState = {
  id: undefined,
  isLoading: undefined,
  hasError: false,
};

export type RequestData<T> = {
  data: T | undefined;
  request: RequestState;
};

export function isFailedRequest<T>(entry: RequestData<T> | undefined): boolean {
  return entry?.request.hasError ?? false;
}

export function isAwaitingData<T>({ data, request }: RequestData<T>): boolean {
  return request.isLoading !== false && data === undefined;
}

const requestSlice = createSlice({
  name: 'request',
  initialState: initialRequestState,
  reducers: {
    fulfilled(state, { payload: { meta } }) {
      if (state.isLoading && state.id === meta.requestId) {
        state.id = undefined;
        state.isLoading = false;
        state.hasError = false;
      }
    },
    pending(state, { payload: { meta } }) {
      state.id = meta.requestId;
      state.isLoading = true;
      state.hasError = false;
    },
    rejected(state, { payload: { meta } }) {
      if (state.isLoading && state.id === meta.requestId) {
        state.id = undefined;
        state.isLoading = false;
        state.hasError = true;
      }
    },
  },
});

export const { fulfilled, pending, rejected } = requestSlice.actions;

export const requestActionsByType = {
  fulfilled,
  pending,
  rejected,
};

export const { reducer: requestReducer } = requestSlice;

/* A per-key cache of independently-tracked requests - lets one slice hold many
 * concurrent loads (e.g. one entry per id) without them clobbering each other. */
export type KeyedRequestData<T> = Record<string, RequestData<T>>;

type RequestActionMeta = { requestId: string };

function requestDataFor<T>(
  cache: KeyedRequestData<T>,
  key: string
): RequestData<T> {
  return cache[key] ?? { data: undefined, request: initialRequestState };
}

export function keyedRequestPending<T>(
  cache: KeyedRequestData<T>,
  key: string,
  meta: RequestActionMeta
): void {
  cache[key] = {
    data: undefined,
    request: requestReducer(
      requestDataFor(cache, key).request,
      pending({ meta })
    ),
  };
}

export function keyedRequestFulfilled<T>(
  cache: KeyedRequestData<T>,
  key: string,
  meta: RequestActionMeta,
  data: T
): void {
  const entry = requestDataFor(cache, key);
  if (entry.request.id === meta.requestId) {
    cache[key] = {
      data,
      request: requestReducer(entry.request, fulfilled({ meta })),
    };
  }
}

export function keyedRequestRejected<T>(
  cache: KeyedRequestData<T>,
  key: string,
  meta: RequestActionMeta
): void {
  const entry = requestDataFor(cache, key);
  cache[key] = {
    data: entry.data,
    request: requestReducer(entry.request, rejected({ meta })),
  };
}
